import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _createForOfIteratorHelper from "@babel/runtime/helpers/createForOfIteratorHelper";
import _objectSpread from "@babel/runtime/helpers/objectSpread2";
var _excluded = ["submodules", "__typename"],
  _excluded2 = ["__typename"];
import { useFeatureFlag } from '@crehana/feature-flags/react';
import { useDebugger } from "../../../debugger";
import { isProduction } from "../../../../constants";
import { FEATURE_FLAGS_V5 } from "../../feature-flags";
import { COLLABORATOR_LEARNING_MODULE } from "../data/full-suite-links";
import { arrayDifference, getAllCodes } from "../data/utils";
import { useConfiguration } from "../use-configuration";
import { useExcludedCodes } from "../use-menu-links/use-excluded-codes";
import { useGetModulesByOrganizationSlug } from "./use-get-modules-by-organization-slug";
var TALENT_MODULES_TO_HIDE = ['MY_TEAM', 'COLLABORATOR_RESULTS', 'RECOGNITION', 'PERFORMANCE', 'CLIMATE_ENGAGEMENT', 'Talent', 'MAIN_ADMIN_ANALYTICS.ADMIN_ANALYTICS.Talent', 'MAIN_ADMIN_ANALYTICS.ADMIN_REPORTS_ANALYTICS.Talent'];
var HRIS_MODULES_TO_HIDE = {
  collaborator: ['MODULE_MANAGEMENT' // This is enough since we use startsWith
  ],
  admin: ['MODULE_MANAGEMENT.CORPORATIVE_DOCUMENTS', 'MODULE_MANAGEMENT.AUTOMATIONS.WORKFLOWS', 'MODULE_MANAGEMENT.TIME_OFF', 'MODULE_MANAGEMENT.TIME_OFF.POLICIES', 'MODULE_MANAGEMENT.TIME_OFF.REQUESTS', 'MODULE_MANAGEMENT.TIME_OFF.HOLIDAYS', 'MODULE_MANAGEMENT.TIME_OFF.CALENDAR', 'MODULE_MANAGEMENT.TIME_OFF.CONFIGURATION']
};

/*
  This hooks handles the logic to get the modules using organizationSlug or companyId based on the available data
*/
export var useTalentModules = function useTalentModules(_ref) {
  var email = _ref.email,
    organizationId = _ref.organizationId,
    organizationSlug = _ref.organizationSlug,
    isKnowledgeHubEnabled = _ref.isKnowledgeHubEnabled,
    sidebarType = _ref.sidebarType,
    isLearningModuleActive = _ref.isLearningModuleActive;
  var modules = useGetModulesByOrganizationSlug({
    email: email,
    organizationSlug: organizationSlug
  });
  var _useExcludedCodes = useExcludedCodes({
      organizationId: organizationId,
      organizationSlug: organizationSlug,
      email: email,
      isKnowledgeHubEnabled: isKnowledgeHubEnabled
    }),
    learningCodesToExclude = _useExcludedCodes.codesToExclude,
    loadingCodesToExclude = _useExcludedCodes.isLoading;

  // Feature flags
  var _useFeatureFlag = useFeatureFlag(FEATURE_FLAGS_V5.ENABLE_HANA_END_USER),
    isHanaEndUserEnabled = _useFeatureFlag.flagValue,
    loadingHanaEndUser = _useFeatureFlag.loading;
  var _useFeatureFlag2 = useFeatureFlag(FEATURE_FLAGS_V5.MODULE_HANA_AI),
    isLegacyHanaEnabled = _useFeatureFlag2.flagValue,
    loadingLegacyHana = _useFeatureFlag2.loading;
  var _useFeatureFlag3 = useFeatureFlag(FEATURE_FLAGS_V5.ENABLE_HRIS_GROUPS_MODULE),
    isHRISGroupsEnabled = _useFeatureFlag3.flagValue,
    loadingHRISGroups = _useFeatureFlag3.loading;
  var _useFeatureFlag4 = useFeatureFlag(FEATURE_FLAGS_V5.HIDE_TALENT_MODULES),
    shouldHideTalentModules = _useFeatureFlag4.flagValue,
    loadingHideTalentModules = _useFeatureFlag4.loading;
  var _useFeatureFlag5 = useFeatureFlag(FEATURE_FLAGS_V5.HIDE_HRIS_MODULES),
    shouldHideHRISModules = _useFeatureFlag5.flagValue,
    loadingHideHRISModules = _useFeatureFlag5.loading;
  var _useFeatureFlag6 = useFeatureFlag(FEATURE_FLAGS_V5.ENABLE_SOCIAL_HUB_NEWS),
    shouldShowSocialHubNews = _useFeatureFlag6.flagValue,
    loadingShowSocialHubNews = _useFeatureFlag6.loading;
  var _useFeatureFlag7 = useFeatureFlag(FEATURE_FLAGS_V5.TALENT_SIDE_MENU_NAVIGATION_V2),
    isNewTalentNavigationActive = _useFeatureFlag7.flagValue,
    loadingNewTalentNavigationFF = _useFeatureFlag7.loading;
  var _useConfiguration = useConfiguration({
      organizationSlug: organizationSlug
    }),
    canUseShop = _useConfiguration.canUseShop;
  var _useDebugger = useDebugger(),
    showHRISLinks = _useDebugger.showHRISLinks;
  var forceShowHRISLinks = showHRISLinks;

  // If ENABLE_HANA_END_USER is enabled, use it regardless of MODULE_HANA_AI
  // If ENABLE_HANA_END_USER is disabled, fallback to MODULE_HANA_AI
  var isHanaEnabled = isHanaEndUserEnabled || !isHanaEndUserEnabled && isLegacyHanaEnabled;
  var adminModulesData = processAdminModules({
    modules: modules.adminModules,
    isHanaActive: isLegacyHanaEnabled && !isHanaEndUserEnabled,
    canUseShop: canUseShop,
    forceShowHRISLinks: forceShowHRISLinks,
    shouldHideTalentModules: shouldHideTalentModules,
    shouldHideHRISModules: shouldHideHRISModules,
    shouldShowSocialHubNews: shouldShowSocialHubNews,
    isHanaAIModuleEnabled: isHanaEnabled,
    isHanaEndUserEnabled: isHanaEndUserEnabled,
    isHRISGroupsEnabled: isHRISGroupsEnabled
  });

  // Collaborator modules
  var collaboratorModulesData = processCollaboratorModules({
    modules: modules.collaboratorModules,
    forceShowHRISLinks: forceShowHRISLinks,
    isLearningModuleActive: isLearningModuleActive,
    learningCodesToExclude: learningCodesToExclude,
    shouldHideTalentModules: shouldHideTalentModules,
    shouldHideHRISModules: shouldHideHRISModules,
    shouldShowSocialHubNews: shouldShowSocialHubNews,
    isHanaAIModuleEnabled: isHanaEnabled,
    isHanaEndUserEnabled: isHanaEndUserEnabled,
    isNewTalentNavigationActive: isNewTalentNavigationActive,
    organizationSlug: organizationSlug,
    isHRISGroupsEnabled: isHRISGroupsEnabled
  });
  return {
    flatModulesData: sidebarType === 'collaborator' ? collaboratorModulesData : adminModulesData,
    loading: Boolean(modules.loading || loadingCodesToExclude || loadingHanaEndUser || loadingHRISGroups || loadingLegacyHana || loadingHideTalentModules || loadingHideHRISModules || loadingShowSocialHubNews || loadingNewTalentNavigationFF)
  };
};
var processCollaboratorModules = function processCollaboratorModules(_ref2) {
  var modules = _ref2.modules,
    forceShowHRISLinks = _ref2.forceShowHRISLinks,
    isLearningModuleActive = _ref2.isLearningModuleActive,
    learningCodesToExclude = _ref2.learningCodesToExclude,
    shouldHideTalentModules = _ref2.shouldHideTalentModules,
    shouldHideHRISModules = _ref2.shouldHideHRISModules,
    isHanaAIModuleEnabled = _ref2.isHanaAIModuleEnabled,
    isHanaEndUserEnabled = _ref2.isHanaEndUserEnabled,
    isNewTalentNavigationActive = _ref2.isNewTalentNavigationActive,
    organizationSlug = _ref2.organizationSlug;
  var _flatModulesData = extractModulesData(modules);

  // ===============
  // we are migrating some modules to a new talent microfrontend where there is no need to use a iframe and query params
  // this new microfrontend lives in the url `/org/[slug]/talent/`
  if (isNewTalentNavigationActive && _flatModulesData['PERFORMANCE.PID'] && organizationSlug) {
    _flatModulesData['PERFORMANCE.PID'] = _objectSpread(_objectSpread({}, _flatModulesData['PERFORMANCE.PID']), {}, {
      url: "".concat(window.location.origin, "/org/").concat(organizationSlug, "/talent/panel/employee/performance/pid/")
    });
  }
  // ===============

  if (_flatModulesData['ANALYTICS.LEADER_RESULTS']) {
    _flatModulesData['MY_TEAM_MODULE'] = {
      code: 'MY_TEAM_MODULE',
      label: null,
      url: null
    };

    // And its children
    _flatModulesData['MY_TEAM_MODULE.FOLLOW_UP'] = {
      code: 'MY_TEAM_MODULE.FOLLOW_UP',
      label: null,
      url: generateSSOUrl('/employee/dashboard-leader')
    };
  }

  // And its children
  _flatModulesData['COMPANY.COMPANYCHART'] = {
    code: 'COMPANY.COMPANYCHART',
    label: null,
    url: '/org/[orgSlug]/hris/org-chart/'
  };
  if (forceShowHRISLinks) {
    var HRIS_LINKS = [];
    for (var _i = 0, _HRIS_LINKS = HRIS_LINKS; _i < _HRIS_LINKS.length; _i++) {
      var link = _HRIS_LINKS[_i];
      _flatModulesData[link] = {
        code: link,
        label: null,
        url: null
      };
    }
  }

  // Process Learning modules
  if (isLearningModuleActive) {
    _flatModulesData['MODULE_LEARNING'] = {
      code: 'MODULE_LEARNING',
      label: 'Aprendizaje',
      url: null
    };
    // Calculate the allowed codes using the whole list of learning codes minus
    // the excluded codes
    var ALLOWED_LEARNING_CODES = arrayDifference(getAllCodes(COLLABORATOR_LEARNING_MODULE.children || []), learningCodesToExclude);
    var _iterator = _createForOfIteratorHelper(ALLOWED_LEARNING_CODES),
      _step;
    try {
      for (_iterator.s(); !(_step = _iterator.n()).done;) {
        var code = _step.value;
        _flatModulesData[code] = {
          code: code,
          label: null,
          url: null
        };
      }
    } catch (err) {
      _iterator.e(err);
    } finally {
      _iterator.f();
    }
  }

  // Add Hana AI module for collaborator only if ENABLE_HANA_END_USER is true
  if (isHanaAIModuleEnabled && isHanaEndUserEnabled) {
    _flatModulesData['HANA_AI'] = {
      code: 'HANA_AI',
      label: 'Hana AI',
      url: '/org/[orgSlug]/ai/user/chat/'
    };
  }

  // Links that should be rendered from now on for this sidebar
  var DEFAULT_LINKS_CODES = ['MODULE_MANAGEMENT', 'MODULE_MANAGEMENT.DOCUMENTS', 'MODULE_MANAGEMENT.TIME_OFF', 'MODULE_MANAGEMENT.TIME_OFF.REQUEST', 'MODULE_MANAGEMENT.TIME_OFF.REQUESTS_LIST', 'MODULE_MANAGEMENT.TIME_OFF.CALENDAR'];

  // Add modules to _flatModulesData
  DEFAULT_LINKS_CODES.forEach(function (code) {
    _flatModulesData[code] = {
      code: code,
      label: null,
      url: null
    };
  });
  if (shouldHideTalentModules) {
    _flatModulesData = Object.fromEntries(Object.entries(_flatModulesData).filter(function (_ref3) {
      var _ref4 = _slicedToArray(_ref3, 1),
        key = _ref4[0];
      return !TALENT_MODULES_TO_HIDE.includes(key) && !TALENT_MODULES_TO_HIDE.some(function (moduleCode) {
        return key.includes(moduleCode);
      });
    }));
  }
  if (shouldHideHRISModules) {
    _flatModulesData = Object.fromEntries(Object.entries(_flatModulesData).filter(function (_ref5) {
      var _ref6 = _slicedToArray(_ref5, 1),
        key = _ref6[0];
      return !key.startsWith('MODULE_MANAGEMENT');
    }));
  }
  return _flatModulesData;
};
var processAdminModules = function processAdminModules(params) {
  var modules = params.modules,
    isHanaActive = params.isHanaActive,
    canUseShop = params.canUseShop,
    forceShowHRISLinks = params.forceShowHRISLinks,
    shouldHideTalentModules = params.shouldHideTalentModules,
    shouldHideHRISModules = params.shouldHideHRISModules,
    shouldShowSocialHubNews = params.shouldShowSocialHubNews,
    isHanaAIModuleEnabled = params.isHanaAIModuleEnabled,
    isHanaEndUserEnabled = params.isHanaEndUserEnabled,
    isHRISGroupsEnabled = params.isHRISGroupsEnabled;

  // Legacy Hana AI module structure for admin
  var LEGACY_HANA_AI_MODULE = {
    type: 'url',
    code: 'HANA_AI',
    label: 'Hana',
    url: '/org/[orgSlug]/panel/hana-ai/',
    tooltip: {
      title: 'Hana AI',
      description: 'Analiza la data de tu compañía con Inteligencia Artificial.'
    }
  };
  var _flatModulesData = extractModulesData(modules);

  // Add Hana AI module based on feature flags
  if (isHanaAIModuleEnabled) {
    if (isHanaEndUserEnabled) {
      // Use the full module structure from admin-links.ts
      _flatModulesData['HANA_AI'] = {
        code: 'HANA_AI',
        label: 'Hana AI',
        url: null
      };
      _flatModulesData['HANA_AI.GENERATE_QUERY'] = {
        code: 'HANA_AI.GENERATE_QUERY',
        label: 'Generar consulta',
        url: '/org/[orgSlug]/ai/admin/chat/'
      };
      _flatModulesData['HANA_AI.ASSISTANT'] = {
        code: 'HANA_AI.ASSISTANT',
        label: 'Asistente',
        url: '/org/[orgSlug]/ai/admin/assistant/'
      };

      // _flatModulesData['HANA_AI.ASSISTANT.DASHBOARD'] = {
      //   code: 'HANA_AI.ASSISTANT.DASHBOARD',
      //   label: 'Dashboard',
      //   url: '/org/[orgSlug]/ai/admin/assistant/dashboard/',
      // };

      _flatModulesData['HANA_AI.ASSISTANT.HELP_DESK'] = {
        code: 'HANA_AI.ASSISTANT.HELP_DESK',
        label: 'Centro de ayuda',
        url: '/org/[orgSlug]/ai/admin/assistant/help-desk/'
      };
    } else {
      // Use legacy simple URL structure
      _flatModulesData['HANA_AI'] = LEGACY_HANA_AI_MODULE;
    }
  }

  // Add the parent module if the children automations is available
  if (_flatModulesData['ADMIN.AUTOMATIONS_ADMIN']) {
    // Parent Automations link
    _flatModulesData['MODULE_MANAGEMENT.AUTOMATIONS'] = {
      code: 'MODULE_MANAGEMENT.AUTOMATIONS',
      label: 'Automatizaciones',
      url: null
    };
    // Automations children link - Assignation
    _flatModulesData['ADMIN.AUTOMATIONS_ADMIN'] = {
      code: 'ADMIN.AUTOMATIONS_ADMIN',
      label: 'Asignación de aprendizaje',
      url: "/org/[orgSlug]/panel/administrar/automatizaciones/"
    };
  }
  _flatModulesData['ADMIN.COMPANYPAR.ORGCHART_COMPANY'] = {
    code: 'ADMIN.COMPANYPAR.ORGCHART_COMPANY',
    label: 'Asignación de aprendizaje',
    url: '/org/[orgSlug]/admin/hris/org-chart/'
  };

  // Add Groups module only if ENABLE_HRIS_GROUPS_MODULE is true
  if (isHRISGroupsEnabled) {
    _flatModulesData['ADMIN.COMPANYPAR.GROUPS'] = {
      code: 'ADMIN.COMPANYPAR.GROUPS',
      label: 'Grupos',
      url: '/org/[orgSlug]/admin/company/groups/'
    };
  }

  // HRIS Links
  var HRIS_LINKS = [
  // Management links
  'ADMIN.COMPANYPAR.AREAS_AND_QUARTERS',
  // 'ADMIN.COMPANYPAR.GROUPS',
  'ADMIN.COMPANYPAR.USERS_COMPANY', 'MODULE_MANAGEMENT.CORPORATIVE_DOCUMENTS', 'MODULE_MANAGEMENT.TIME_OFF', 'MODULE_MANAGEMENT.TIME_OFF.POLICIES', 'MODULE_MANAGEMENT.TIME_OFF.REQUESTS', 'MODULE_MANAGEMENT.TIME_OFF.HOLIDAYS', 'MODULE_MANAGEMENT.TIME_OFF.CALENDAR',
  // 'MODULE_MANAGEMENT.TIME_OFF.CONFIGURATION',
  'MODULE_MANAGEMENT.SOCIAL_HUB', 'MODULE_MANAGEMENT.AUTOMATIONS', 'MODULE_MANAGEMENT.AUTOMATIONS.WORKFLOWS'];
  if (forceShowHRISLinks) {
    var _iterator2 = _createForOfIteratorHelper(HRIS_LINKS),
      _step2;
    try {
      for (_iterator2.s(); !(_step2 = _iterator2.n()).done;) {
        var link = _step2.value;
        if (!shouldShowSocialHubNews && link === 'MODULE_MANAGEMENT.SOCIAL_HUB') {
          continue;
        }
        _flatModulesData[link] = {
          code: link,
          label: null,
          url: null
        };
      }
    } catch (err) {
      _iterator2.e(err);
    } finally {
      _iterator2.f();
    }
  }

  // Hana AI
  if (isHanaActive) {
    _flatModulesData['HANA_AI'] = {
      code: 'HANA_AI',
      label: null,
      url: null
    };
  }

  // Shop
  if (canUseShop) {
    _flatModulesData['MODULE_LEARNING.STORE'] = {
      code: 'SHOP_MODULE',
      label: null,
      url: null
    };
    _flatModulesData['SHOP_MODULE'] = {
      code: 'MODULE_LEARNING.STORE.CONFIGURATION',
      label: null,
      url: null
    };
  }

  /**
   *
   * Default links for admin accounts
   * This section adds default modules for admin accounts
   *
   */
  // Define an array of module configurations
  var DEFAULT_LINKS = [
  // General config links
  {
    code: 'ADMIN',
    label: null,
    url: null
  }, {
    code: 'ADMIN.ACCOUNT_ADMIN',
    label: 'Cuenta',
    url: null
  }, {
    code: 'ADMIN.APPEARENCE_ADMIN',
    label: 'Logs',
    url: null
  }, {
    code: 'ADMIN.COMMUNICATION_ADMIN',
    label: 'Comunicaciones',
    url: null
  },
  // This module is always needed to render at least 1 submodule
  {
    code: 'MODULE_GENERAL_CONFIG',
    label: 'Configuración general',
    url: null
  },
  // Management user links
  {
    code: 'ADMIN.COMPANYPAR',
    label: null,
    url: null
  }, {
    code: 'ADMIN.COMPANYPAR.USERS_COMPANY',
    label: 'Usuarios',
    url: "/org/[orgSlug]/admin/company/users/"
  }, {
    code: 'ADMIN.COMPANYPAR.ORGCHART_COMPANY',
    label: 'Organigrama',
    url: generateSSOUrl('/company/employee-admin-chart')
  }, {
    code: 'ADMIN.COMPANYPAR.EXTRA_FIELDS_COMPANY',
    label: 'Atributos personalizados',
    url: "/org/[orgSlug]/admin/company/custom-fields/"
  }, {
    code: 'ADMIN.AUTOMATIONS_ADMIN',
    label: 'Asignación de aprendizaje',
    url: "/org/[orgSlug]/panel/administrar/automatizaciones/"
  },
  // Temporal hardcoded links for Learning Content
  {
    code: 'MODULE_LEARNING.CONTENT.LIVE_SESSIONS'
  }, {
    code: 'MODULE_LEARNING.CONTENT.DOCUMENTS'
  }, {
    code: 'MODULE_LEARNING.CONTENT.CATALOG'
  }, {
    code: 'MODULE_LEARNING.CONTENT.CERTIFICATES'
  }];
  if (!forceShowHRISLinks) {
    DEFAULT_LINKS.push({
      code: 'ADMIN.COMPANYPAR.JOBS_COMPANY',
      label: 'Cargos y categorías',
      url: generateSSOUrl('/company/jobrole')
    });
    DEFAULT_LINKS.push({
      code: 'ADMIN.COMPANYPAR.DIVISION_COMPANY',
      label: 'Departamentos',
      url: generateSSOUrl('/company/departments')
    });
  }

  // Add modules to _flatModulesData
  DEFAULT_LINKS.forEach(function (_link) {
    _flatModulesData[_link.code] = {
      code: _link.code,
      label: _link.label,
      url: _link.url
    };
  });
  if (shouldHideTalentModules) {
    _flatModulesData = Object.fromEntries(Object.entries(_flatModulesData).filter(function (_ref7) {
      var _ref8 = _slicedToArray(_ref7, 1),
        key = _ref8[0];
      return !TALENT_MODULES_TO_HIDE.includes(key) && !TALENT_MODULES_TO_HIDE.some(function (moduleCode) {
        return key.includes(moduleCode);
      });
    }));
  }
  if (shouldHideHRISModules) {
    _flatModulesData = Object.fromEntries(Object.entries(_flatModulesData).filter(function (_ref9) {
      var _ref0 = _slicedToArray(_ref9, 1),
        key = _ref0[0];
      return !HRIS_MODULES_TO_HIDE.admin.includes(key) && !HRIS_MODULES_TO_HIDE.admin.some(function (moduleCode) {
        return key.includes(moduleCode);
      });
    }));
  }
  return _flatModulesData;
};
var extractModulesData = function extractModulesData(modules) {
  var moduleData = {};
  var _extractDataRecursive = function extractDataRecursive(item) {
    var parentCode = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : '';
    var submodules = item.submodules,
      __typename = item.__typename,
      moduleWithoutSubmodulesAndTypename = _objectWithoutProperties(item, _excluded);
    var currentCode = item.code;
    if (parentCode) {
      currentCode = "".concat(parentCode, ".").concat(currentCode);
    }
    moduleData[currentCode] = _objectSpread(_objectSpread({}, moduleWithoutSubmodulesAndTypename), {}, {
      code: currentCode
    });
    if (submodules && submodules.length > 0) {
      submodules.forEach(function (submodule) {
        if ('submodules' in submodule) {
          _extractDataRecursive(submodule, currentCode);
        } else if (submodule.code) {
          var _typename = submodule.__typename,
            submoduleWithoutTypename = _objectWithoutProperties(submodule, _excluded2);
          moduleData["".concat(currentCode, ".").concat(submodule.code)] = _objectSpread(_objectSpread({}, submoduleWithoutTypename), {}, {
            code: "".concat(currentCode, ".").concat(submodule.code)
          });
        }
      });
    }
  };
  modules.forEach(function (module) {
    return _extractDataRecursive(module);
  });

  // This module is always needed to render any product
  moduleData['PRODUCTS_MODULE'] = {
    code: 'PRODUCTS_MODULE',
    url: null
  };
  moduleData['MODULE_HELP'] = {
    code: 'MODULE_HELP',
    url: null
  };
  return moduleData;
};
var generateSSOUrl = function generateSSOUrl(path) {
  var HOST = isProduction ? 'https://crehana.acsendo.com' : 'https://pruebas.acsendo.com';
  return "".concat(HOST, "/rest/saml3/login?next=").concat(HOST).concat(path);
};